import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore, type User } from "../store/auth";
import { api, ApiError } from "../lib/api";
import { LanguageMenu } from "../components/LanguageMenu";
import { WorldIntroPanel } from "../components/WorldIntroPanel";

type LoginResponse = { user: User; access_token: string };

const DEMO_ACCOUNTS = [
  { email: "anna@demo.nihongo-platform.jp",   name: "Anna Schmidt",  level: "Lv 1", noteKey: "demo_note_beginner" },
  { email: "carlos@demo.nihongo-platform.jp", name: "Carlos Garcia", level: "Lv 1", noteKey: "demo_note_hiragana_progress" },
  { email: "lin@demo.nihongo-platform.jp",    name: "Lin Wei",       level: "Lv 1", noteKey: "demo_note_hiragana_master" },
  { email: "pierre@demo.nihongo-platform.jp", name: "Pierre Dubois", level: "Lv 2", noteKey: "demo_note_streak_7d" },
  { email: "emma@demo.nihongo-platform.jp",   name: "Emma Johnson",  level: "Lv 3", noteKey: "demo_note_kana_master" },
  { email: "mei@demo.nihongo-platform.jp",    name: "Mei Tanaka",    level: "Lv 5", noteKey: "demo_note_advanced" },
];

export function LoginPage() {
  const { t } = useTranslation(["common", "learner"]);
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [demoOpen, setDemoOpen] = useState(true);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await api<LoginResponse>("/api/auth/login", { method: "POST", body: { identifier: identifier.trim(), password } });
      setAuth(data.access_token, data.user);
      navigate("/");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common:error_login_failed"));
    } finally {
      setLoading(false);
    }
  }

  function fillDemo(demoEmail: string) {
    setIdentifier(demoEmail);
    setPassword("demo1234");
  }

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-8 bg-gradient-to-br from-sky-50 via-white to-sakura/20">
      <div className="w-full max-w-sm flex justify-end mb-3">
        <LanguageMenu variant="full" />
      </div>
      <div className="w-full max-w-sm">
        <WorldIntroPanel />
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg p-8">
          <h2 className="text-xl font-bold text-primary mb-6 text-center">{t("common:login")}</h2>
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="text-sm text-muted">{t("common:login_identifier")}</span>
              <input type="text" name="identifier" data-testid="login-identifier" required autoComplete="username"
                autoCapitalize="none" spellCheck={false}
                value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-sm text-muted">{t("common:password")}</span>
              <input type="password" name="password" data-testid="login-password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none" />
            </label>
            {error && <p data-testid="login-error" role="alert" className="text-sm text-accent">{error}</p>}
            <button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50">
              {loading ? t("common:loading") : t("common:login")}
            </button>
          </form>
        </div>

        <div className="bg-white/95 backdrop-blur rounded-2xl shadow mt-4 overflow-hidden">
          <button
            type="button"
            onClick={() => setDemoOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50"
          >
            <span className="text-sm font-bold text-primary">{t("learner:demo_section_title")}</span>
            <span className="text-muted text-xs">{demoOpen ? "▲" : "▼"}</span>
          </button>
          {demoOpen && (
            <div className="border-t border-gray-100 divide-y divide-gray-100">
              <p className="px-5 py-2 text-xs text-muted bg-gray-50">
                {t("learner:demo_section_hint", { pw: "demo1234" })}
              </p>
              {DEMO_ACCOUNTS.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => fillDemo(d.email)}
                  className="w-full px-5 py-3 text-left hover:bg-sakura/20 flex items-center justify-between"
                >
                  <div>
                    <div className="text-sm font-bold text-body">{d.name}</div>
                    <div className="text-xs text-muted">{d.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold text-primary">{d.level}</div>
                    <div className="text-xs text-muted">{t(`learner:${d.noteKey}`)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
